import { Component, OnInit, OnDestroy, TemplateRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';

import { AuthService } from '../../../services/common/auth.service';
import { NotyService } from '../../../services/common/noty.service';
import { ReminderService } from '../../../services/events/reminder.service';

@Component({
  selector: 'app-dashboard-reminder',
  templateUrl: './dashboard-reminder.component.html',
  styleUrls: ['./dashboard-reminder.component.css'],
})
export class DashboardReminderComponent implements OnInit, OnDestroy {
  @ViewChild('reminderModal') reminderModal: TemplateRef<any>;
  @Output() remindersDismissed = new EventEmitter<number>();

  reminders: Array<any> = [];
  loading: boolean = false;
  loadError: boolean = false;
  filter: string = 'pending';
  modalRef: BsModalRef;
  selectedReminder: any = null;
  userId: string;
  selectedIds: Set<number> = new Set();
  bulkDismissing: boolean = false;
  private loadSequence: number = 0;
  private readonly requestTimeoutMs: number = 20000;

  constructor(
    private authService: AuthService,
    private reminderService: ReminderService,
    private notyService: NotyService,
    private modalService: BsModalService,
    private router: Router
  ) {}

  ngOnInit() {
    this.authService.getCurrentUserGroups().then((res) => {
      this.userId = res.current_user_id.toString();
      this.loadReminders();
    });
  }

  ngOnDestroy() {
    if (this.modalRef) this.modalRef.hide();
  }

  loadReminders(type: string = this.filter) {
    this.filter = type;
    this.loading = true;
    this.loadError = false;
    this.selectedIds.clear();
    const sequence = ++this.loadSequence;

    this.withTimeout(this.reminderService.index2({
      type: this.filter,
      angular2: true,
      user_id: this.userId
    }), this.requestTimeoutMs).then((response) => {
      if (sequence !== this.loadSequence) return;
      this.loading = false;
      this.reminders = (response.reminders || []).map((r: any) => ({
        ...r,
        topic_short: r.topic?.length > 40 ? r.topic.slice(0, 37) + '...' : r.topic,
        title_short: r.reminderable_title?.length > 25 ? r.reminderable_title.slice(0, 22) + '...' : r.reminderable_title,
        tooltip: [r.reminderable_title, r.topic, r.body].filter(Boolean).join('\n')
      }));
    }).catch(() => {
      if (sequence !== this.loadSequence) return;
      this.loading = false;
      this.loadError = true;
      this.reminders = [];
    });
  }

  private withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    let timeoutHandle: any;
    const timeoutPromise = new Promise<T>((_resolve, reject) => {
      timeoutHandle = setTimeout(() => reject(new Error('Request timed out')), timeoutMs);
    });
    return Promise.race([promise, timeoutPromise]).then((response: T) => {
      clearTimeout(timeoutHandle);
      return response;
    }).catch((error) => {
      clearTimeout(timeoutHandle);
      throw error;
    });
  }

  dismiss(reminder: any, event: Event) {
    event.stopPropagation();
    reminder.dismissing = true;
    this.reminderService.dismiss(reminder.id).then((res) => {
      reminder.dismissing = false;
      if (res.success) {
        this.notyService.notify('Reminder dismissed', 'success', 3000, 'center');
        this.remindersDismissed.emit(1);
        this.loadReminders();
      } else {
        this.notyService.notify(res.text || 'Error', 'error', 3000, 'center');
      }
    }).catch(() => {
      reminder.dismissing = false;
      this.notyService.notify('Error dismissing', 'error', 3000, 'center');
    });
  }

  toggleSelection(reminder: any, event: Event) {
    event.stopPropagation();
    if (this.selectedIds.has(reminder.id)) {
      this.selectedIds.delete(reminder.id);
    } else {
      this.selectedIds.add(reminder.id);
    }
  }

  toggleSelectAll(event: Event) {
    event.stopPropagation();
    if (this.selectedIds.size === this.reminders.length) {
      this.selectedIds.clear();
    } else {
      this.reminders.forEach(r => this.selectedIds.add(r.id));
    }
  }

  isSelected(reminder: any): boolean {
    return this.selectedIds.has(reminder.id);
  }

  get allSelected(): boolean {
    return this.reminders.length > 0 && this.selectedIds.size === this.reminders.length;
  }

  bulkDismiss() {
    if (this.selectedIds.size === 0) return;

    this.bulkDismissing = true;
    const ids = Array.from(this.selectedIds);
    const promises = ids.map(id => this.reminderService.dismiss(id));

    Promise.all(promises).then((results) => {
      this.bulkDismissing = false;
      const successCount = results.filter(r => r.success).length;
      if (successCount > 0) {
        this.notyService.notify(`${successCount} reminder${successCount > 1 ? 's' : ''} dismissed`, 'success', 3000, 'center');
        this.remindersDismissed.emit(successCount);
        this.loadReminders();
      }
    }).catch(() => {
      this.bulkDismissing = false;
      this.notyService.notify('Error dismissing reminders', 'error', 3000, 'center');
    });
  }

  openReminder(reminder: any) {
    this.selectedReminder = reminder;
    this.modalRef = this.modalService.show(this.reminderModal, { class: 'modal-lg' });
  }

  goToReminderable(reminder: any) {
    if (this.modalRef) this.modalRef.hide();
    if (reminder.reminderable_url) {
      this.router.navigateByUrl(reminder.reminderable_url);
    }
  }

  newReminder() {
    this.router.navigateByUrl('/reminders?new=true');
  }

  editReminder(reminder: any) {
    if (this.modalRef) this.modalRef.hide();
    // Navigate to the matter page where reminders can be edited
    if (reminder.matter_id) {
      this.router.navigateByUrl(`/matters/${reminder.matter_id}?tab=reminders&reminder_id=${reminder.id}`);
    } else if (reminder.reminderable_url) {
      this.router.navigateByUrl(reminder.reminderable_url);
    }
  }
}
